// src/components/planner/modals/EditMonthEventModal.tsx

import React, { useState } from 'react';
import { X, Save } from 'lucide-react';

const API_BASE_URL = '/api';

interface EditMonthEventModalProps {
    event: {
        _id: string;
        title: string;
        type: string;
        date: string;
        time?: string;
        color: string;
        notes?: string;
    };
    onClose: () => void;
    onSave: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const EditMonthEventModal: React.FC<EditMonthEventModalProps> = ({
    event,
    onClose,
    onSave,
    onShowToast
}) => {
    const [formData, setFormData] = useState({
        title: event.title,
        type: event.type,
        customType: '',
        date: new Date(event.date).toISOString().split('T')[0],
        time: event.time || '09:00',
        color: event.color,
        notes: event.notes || ''
    });

    const colors = [
        '#003BD1', '#2563eb', '#9333ea', '#ea580c',
        '#dc2626', '#10b956', '#ca8a04', '#6B7280'
    ];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            onShowToast('Preencha o título', 'error');
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/month-events/${event._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...formData,
                    type: formData.type === 'other' ? 'other' : formData.type,
                    customType: formData.type === 'other' ? formData.customType : undefined,
                })
            });

            if (response.ok) {
                onShowToast('Evento atualizado!', 'success');
                onSave();
            } else {
                onShowToast('Erro ao atualizar evento', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao atualizar evento', 'error');
        }
    };

    return (
        <div className="plnr-modal-overlay" onClick={onClose}>
            <div className="plnr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="plnr-modal-header">
                    <h3>Editar Evento</h3>
                    <button className="plnr-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="plnr-modal-body">
                        <div className="plnr-form-group">
                            <label>Título *</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Ex: Congresso de Cardiologia"
                                className="plnr-input"
                                required
                            />
                        </div>

                        <div className="plnr-form-group">
                            <label>Tipo de evento</label>
                            <select
                                value={formData.type}
                                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                className="plnr-input"
                            >
                                <option value="event">Evento</option>
                                <option value="congress">Congresso</option>
                                <option value="workshop">Workshop</option>
                                <option value="exam">Prova</option>
                                <option value="meeting">Reunião</option>
                                <option value="lecture">Palestra</option>
                                <option value="extracurricular">Atividade Extracurricular</option>
                                <option value="tutoring">Monitoria</option>
                                <option value="mock_exam">Simulado</option>
                                <option value="other">Outro</option>
                            </select>
                        </div>

                        {formData.type === 'other' && (
                            <div className="plnr-form-group">
                                <label>Nome do tipo</label>
                                <input
                                    type="text"
                                    value={formData.customType}
                                    onChange={(e) => setFormData({ ...formData, customType: e.target.value })}
                                    className="plnr-input"
                                />
                            </div>
                        )}

                        <div className="plnr-form-row">
                            <div className="plnr-form-group">
                                <label>Data</label>
                                <input
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                    className="plnr-input"
                                />
                            </div>

                            <div className="plnr-form-group">
                                <label>Horário</label>
                                <input
                                    type="time"
                                    value={formData.time}
                                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                                    className="plnr-input"
                                />
                            </div>
                        </div>

                        <div className="plnr-form-group">
                            <label>Cor</label>
                            <div className="plnr-color-grid">
                                {colors.map(color => (
                                    <button
                                        key={color}
                                        type="button"
                                        className={`plnr-color-option ${formData.color === color ? 'active' : ''}`}
                                        style={{ backgroundColor: color }}
                                        onClick={() => setFormData({ ...formData, color })}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="plnr-form-group">
                            <label>Notas (opcional)</label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Detalhes sobre o evento..."
                                className="plnr-textarea"
                                rows={3}
                            />
                        </div>
                    </div>

                    <div className="plnr-modal-footer">
                        <button type="button" className="plnr-btn secondary" onClick={onClose}>
                            Cancelar
                        </button>
                        <button type="submit" className="plnr-btn primary">
                            <Save size={16} />
                            Salvar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditMonthEventModal;